import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Table,
} from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { notifyError, notifySuccess, priceFormat } from '../../store/utils';
import CustomerInput from '../../lib/CustomerInput';
import AddressCascader from '../../lib/AddressCascader';
import { useParams } from 'react-router-dom';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API } from '../../api';
import { UserContext } from '../../context/UserProvider';

const formItemLayout = {
  labelCol: {
    md: { span: 8 },
  },
  wrapperCol: {
    md: { span: 16 },
  },
};

interface ItemValue {
  key: number;
  no: number;
  itemType: string;
  warningNo: number;
  weightTypeNo: number;
  quantity: number;
  deliveryCost: number;
  additionalCost: number;
  weight: number;
}

const defaultPrice = 5000;
const defaultWeightTypeNo = 1;
const defaultWarningNo = 1;
const defaultDeliveryType = 'HOMEDELIVERY';

const initialItemValue: ItemValue = {
  key: 1,
  no: 0,
  itemType: '',
  warningNo: defaultWarningNo,
  weightTypeNo: defaultWeightTypeNo,
  quantity: 1,
  deliveryCost: defaultPrice,
  additionalCost: 0,
  weight: 0,
};

interface FormData {
  customer: any | null;
  warningList: any[];
  weightTypeList: any[];
  paymentTypeList: any[];
  deliveryTypeList: any[];
}

const OrderUpdate = () => {
  const { getCustomer } = useContext(UserContext);
  const { orderNo } = useParams();
  const [form] = Form.useForm();
  const [loading, updateLoading] = useState(false);
  const [items, setItems] = useState<ItemValue[]>([initialItemValue]);

  const [data, setData] = useState<FormData>({
    customer: null,
    warningList: [],
    weightTypeList: [],
    paymentTypeList: [],
    deliveryTypeList: [],
  });

  const senderZipcodes = Form.useWatch('senderZipcodes', form);
  const receiverZipcodes = Form.useWatch('receiverZipcodes', form);
  const deliveryType = Form.useWatch('deliveryType', form);

  const [sameAddress, setSameAddress] = useState(false);

  const fetchOrderDetail = useCallback(async () => {
    try {
      const {
        result: { data },
      } = await API.get({ apiVersion: 'api' })(`/order/${orderNo}`);

      const senderStreet = data?.senderStreet || {};
      const receiverStreet = data?.receiverStreet || {};
      form.setFieldsValue({
        customerNo: data?.customer?.no,
        senderFormVisibility: true,
        senderFname: data?.senderFname,
        senderPhone1: data?.senderPhone1 || undefined,
        senderPhone2: data?.senderPhone2 || undefined,
        senderDetail: data?.senderAddressDetail,
        senderW3w: data?.senderW3w,
        senderZipcodes: [senderStreet?.province?.no, senderStreet?.district?.no, senderStreet.no],
        receiverFname: data?.receiverFname,
        receiverPhone1: data?.receiverPhone1 || undefined,
        receiverPhone2: data?.receiverPhone2 || undefined,
        receiverDetail: data?.receiverAddressDetail,
        receiverW3w: data?.receiverW3w,
        receiverZipcodes: [
          receiverStreet?.province?.no,
          receiverStreet?.district?.no,
          receiverStreet.no,
        ],
        deliveryType: data?.deliveryType?.code || defaultDeliveryType,
        paymentTypeCd: data?.paymentType?.code || 'CONTRACT',
        targetDate: dayjs(data.targetDate),
      });

      const itemList: ItemValue[] = (data?.items || []).map((item: any, index: number) => {
        return {
          key: index + 1,
          no: item?.no,
          itemType: item?.itemType,
          warningNo: item?.warning?.no || defaultWarningNo,
          weightTypeNo: item?.weightType?.no || defaultWeightTypeNo,
          quantity: 1,
          deliveryCost: item?.deliveryConst || defaultPrice,
          additionalCost: item?.additionalCost || 0,
          weight: item?.weight,
        };
      });
      setItems(itemList);
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [orderNo]);

  useEffect(() => {
    if (orderNo) {
      fetchOrderDetail();
    }
  }, [orderNo]);

  const fetchFormData = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(`/order/formdata`);
      setData({
        customer: null,
        warningList: result.warningList,
        weightTypeList: result?.weightTypeList,
        paymentTypeList: result?.paymentTypeList || [],
        deliveryTypeList: result?.deliveryTypeList || [],
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, []);

  const calculateAmount = useCallback(
    async (list: any[]) => {
      try {
        const values = form.getFieldsValue();
        const weightTypeNoList = list.map((item: ItemValue) => item.weightTypeNo);
        if (
          values?.senderZipcodes &&
          values.senderZipcodes.length &&
          values?.receiverZipcodes &&
          values.receiverZipcodes.length
        ) {
          const payload = {
            customerNo: values.customerNo,
            senderStreetNo: values.senderZipcodes.at(-1),
            receiverStreetNo: values.receiverZipcodes.at(-1),
            weightTypeNoList,
            deliveryType: values.deliveryType,
          };

          const { result } = await API.post({ apiVersion: 'api' })(
            `/order/calculate/cost`,
            payload
          );
          const newItems = list.map((item: any) => {
            const price = result.find((p: any) => p.weightTypeNo === item.weightTypeNo) || {
              amount: defaultPrice,
            };
            return {
              ...item,
              deliveryCost: price?.amount,
            };
          });
          setItems(newItems);
        } else {
          const newItems = list.map((item: any) => {
            return {
              ...item,
              deliveryCost: defaultPrice,
            };
          });
          setItems(newItems);
        }
      } catch (error: any) {
        notifyError(error.message);
      }
    },
    [form, items]
  );

  const handleCustomerSelect = useCallback(
    (customer: any) => {
      if (customer?.no) {
        const zipcodes = [
          customer?.street?.province?.no,
          customer?.street?.district?.no,
          customer?.street?.no,
        ];

        form.setFieldsValue({
          customerNo: customer?.no,
          senderFormVisibility: true,
          senderFname: customer.name,
          senderPhone1: customer?.phone1 || undefined,
          senderPhone2: customer?.phone2 || undefined,
          senderDetail: customer?.addressDetail,
          senderW3w: customer?.w3w,
          senderZipcodes: zipcodes,
        });
      } else {
        form.setFieldsValue({
          customerNo: undefined,
          senderFormVisibility: true,
          senderFname: undefined,
          senderPhone1: undefined,
          senderPhone2: undefined,
          senderDetail: undefined,
          senderW3w: undefined,
          senderZipcodes: [],
        });
      }
    },
    [form]
  );

  const sameAddressChecked = useCallback(
    (event: any) => {
      if (event.target.checked) {
        const values = form.getFieldsValue();
        form.setFieldsValue({
          receiverFname: values?.senderFname,
          receiverPhone1: values?.senderPhone1 || undefined,
          receiverPhone2: values?.senderPhone2 || undefined,
          receiverDetail: values?.senderDetail,
          receiverW3w: values?.senderW3w,
          receiverZipcodes: values?.senderZipcodes || [],
        });
      }
      setSameAddress(event.target.checked);
    },
    [sameAddress, form]
  );

  const onFinish = useCallback(
    async (values: any) => {
      try {
        updateLoading(true);
        console.log(values);
        const payload = {
          deliveryType: values.deliveryType,
          paymentTypeCd: values.paymentTypeCd,
          targetDate: values.targetDate,

          customerNo: values.customerNo,
          senderFname: values.senderFname,
          senderPhone1: values.senderPhone1,
          senderPhone2: values.senderPhone2,
          senderDetail: values.senderDetail,
          senderW3w: values.senderW3w,
          senderStreetNo: values.senderZipcodes.at(-1),

          receiverFname: values?.receiverFname,
          receiverPhone1: values?.receiverPhone1,
          receiverPhone2: values?.receiverPhone2 || undefined,
          receiverDetail: values?.receiverDetail,
          receiverW3w: values?.receiverW3w,
          receiverStreetNo: values?.receiverZipcodes.at(-1),
          items: items.map((item: any) => {
            return {
              no: item.no ? item.no : undefined,
              itemType: item.itemType,
              warningNo: item.warningNo,
              weightTypeNo: item.weightTypeNo,
              quantity: item.quantity,
              additionalCost: item.additionalCost,
              weight: item.weight,
            };
          }),
        };

        if (orderNo) {
          const { result } = await API.put({ apiVersion: 'api' })(
            `/order/update/${orderNo}`,
            payload
          );
        } else {
          const { result } = await API.post({ apiVersion: 'api' })(`/order/create`, payload);
          form.setFieldsValue({
            receiverFname: undefined,
            receiverPhone1: undefined,
            receiverPhone2: undefined,
            receiverDetail: undefined,
            receiverW3w: undefined,
            receiverStreetNo: undefined,
          });
          setItems([initialItemValue]);
        }
        notifySuccess('Амжилттай хадгаллаа!');
      } catch (error: any) {
        notifyError(error.message);
      } finally {
        updateLoading(false);
      }
    },
    [items, orderNo]
  );

  const paymentOptions = useMemo(() => {
    //payment iin turul iig label, value bolgono customer role tai baival zuwhun contract
    let options = data?.paymentTypeList.map((pt: any) => {
      return {
        label: pt.text,
        value: pt.code,
      };
    });

    if (getCustomer) {
      options = options.filter((option: any) => option.value === 'CONTRACT');
    }

    return options;
  }, [data, getCustomer]);

  const deliveryTypeOptions = useMemo(() => {
    let options = data?.deliveryTypeList.map((pt: any) => {
      return {
        label: pt.text,
        value: pt.code,
      };
    });

    return options;
  }, [data]);

  const onChangeItemValue = useCallback(
    (key: number, value: any, name: string) => {
      const list = items.map((item: any) => {
        if (item.key === key) {
          return {
            ...item,
            [name]: value,
          };
        } else {
          return item;
        }
      });
      if (name === 'weightTypeNo') {
        calculateAmount(list);
      } else {
        setItems(list);
      }
    },
    [items]
  );

  const removeOrderLineTime = useCallback(
    (key: number) => {
      const list = items.filter((item: any) => item.key !== key);
      setItems(list);
    },
    [items]
  );

  const addOrderLineItem = useCallback(() => {
    let lastItem = items[items.length - 1];
    let lineItem = {
      key: lastItem.key + 1,
      no: 0,
      itemType: '',
      warningNo: defaultWarningNo,
      weightTypeNo: defaultWeightTypeNo,
      quantity: 1,
      deliveryCost: defaultPrice,
      additionalCost: 0,
      weight: 0,
    };
    let newItems = [...items, lineItem];
    calculateAmount(newItems);
  }, [items]);

  useEffect(() => {
    fetchFormData();
  }, []);

  useEffect(() => {
    if (senderZipcodes && receiverZipcodes && deliveryType) {
      calculateAmount(items);
    }
  }, [senderZipcodes, receiverZipcodes, deliveryType]);

  const totalAmount = useMemo(() => {
    const total = items.reduce(
      (accumulator, currentValue) =>
        accumulator +
        (currentValue.deliveryCost + currentValue.additionalCost) * currentValue.quantity,
      0
    );
    return total;
  }, [items]);

  const columns = useMemo(() => {
    return [
      {
        title: '#',
        width: 50,
        key: 'Number',
        render: (val: any, row: any) => <span>{items.indexOf(row) + 1}</span>,
      },
      {
        title: 'Ил.Төрөл',
        dataIndex: 'itemType',
        key: 'itemType',
        render: (val: any, row: any) => (
          <Input
            placeholder="Илгээмжийн төрөл"
            value={val}
            onChange={(event: any) => onChangeItemValue(row.key, event.target.value, 'itemType')}
          />
        ),
      },
      {
        title: 'Анхаарах',
        dataIndex: 'warningNo',
        key: 'warningNo',
        render: (val: any, row: any) => (
          <Select
            style={{ width: '100%' }}
            placeholder="Анхаарах"
            defaultValue={defaultWarningNo}
            value={val}
            onChange={(val) => onChangeItemValue(row.key, val, 'warningNo')}
          >
            {data.warningList.map((item: any) => (
              <Select.Option key={item.no} value={item.no}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        title: 'Хэмжээ',
        dataIndex: 'weightTypeNo',
        key: 'weightTypeNo',
        width: 100,
        render: (val: any, row: any) => (
          <Select
            style={{ width: '100%' }}
            placeholder="Хэмжээ"
            defaultValue={defaultWeightTypeNo}
            value={val}
            onChange={(val) => onChangeItemValue(row.key, val, 'weightTypeNo')}
          >
            {data.weightTypeList.map((item: any) => (
              <Select.Option key={item.no} value={item.no}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        title: 'Тоо.ш',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 80,
        render: (val: any, row: any) => (
          <div>
            {!orderNo ? (
              <InputNumber
                min={1}
                step={1}
                defaultValue={1}
                value={val}
                onChange={(val) => onChangeItemValue(row.key, val, 'quantity')}
              />
            ) : (
              1
            )}
          </div>
        ),
      },
      {
        title: 'Нэгж үнэ',
        key: 'deliveryCost',
        dataIndex: 'deliveryCost',
        width: 80,
        render: (val: any) => priceFormat(val),
      },
      {
        title: 'Нэмэгдэл',
        dataIndex: 'additionalCost',
        key: 'additionalCost',
        width: 60,
        render: (val: any, row: any) => (
          <InputNumber
            min={0}
            step={100}
            defaultValue={0}
            value={val}
            onChange={(val) => onChangeItemValue(row.key, val, 'additionalCost')}
          />
        ),
      },
      {
        title: 'Жин',
        dataIndex: 'weight',
        key: 'weight',
        width: 60,
        render: (val: any, row: any) => (
          <InputNumber
            min={0}
            step={100}
            defaultValue={0}
            value={val}
            onChange={(val) => onChangeItemValue(row.key, val, 'weight')}
          />
        ),
      },
      {
        title: 'Нийт',
        width: 80,
        key: 'totalCost',
        render: (val: any, row: any) => (
          <span>{priceFormat((row.deliveryCost + row.additionalCost) * row.quantity)} </span>
        ),
      },
      {
        title: 'Үйлд.',
        key: 'Action',
        width: 60,
        render: (val: any, row: any) => (
          <div>
            {items.length > 1 ? (
              <Button
                danger
                type="primary"
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => removeOrderLineTime(row.key)}
              ></Button>
            ) : null}
          </div>
        ),
      },
    ];
  }, [items, data]);

  return (
    <Card title={`Захиалга үүсгэх`}>
      <Form
        form={form}
        onFinish={onFinish}
        initialValues={{
          deliveryType: defaultDeliveryType,
          paymentTypeCd: 'CONTRACT',
          targetDate: dayjs(),
        }}
      >
        <Row>
          <Col span={20}>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  colon={false}
                  {...formItemLayout}
                  label="Илгээгч"
                  style={{ marginBottom: 10, fontSize: 24, fontWeight: 'bold' }}
                />
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Row>
                  <Col span={24}>
                    <Form.Item label="Нэр" hidden={true} name="customerNo">
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...formItemLayout}
                      label="Нэр"
                      rules={[{ required: true, message: 'Нэр оруулна уу!' }]}
                      name="senderFname"
                    >
                      <CustomerInput onSelect={handleCustomerSelect} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.senderFormVisibility !== currentValues.senderFormVisibility
                      }
                    >
                      {({ getFieldValue }) =>
                        getFieldValue('senderFormVisibility') ? (
                          <Row gutter={24}>
                            <Col span={24}>
                              <Row>
                                <Col span={16}>
                                  <Form.Item
                                    labelCol={{ md: { span: 12 } }}
                                    wrapperCol={{ md: { span: 12 } }}
                                    label="Утас"
                                    name="senderPhone1"
                                    rules={[{ required: true, message: 'Утас 1 оруулна уу!' }]}
                                  >
                                    <Input placeholder="Утас 1" />
                                  </Form.Item>
                                </Col>
                                <Col span={8}>
                                  <Form.Item
                                    style={{ marginBottom: 10, paddingLeft: 10 }}
                                    name="senderPhone2"
                                  >
                                    <Input placeholder="Утас 2" />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </Col>
                            <Col span={24}>
                              <Form.Item
                                style={{ marginBottom: 10, paddingLeft: 10 }}
                                {...formItemLayout}
                                name="senderZipcodes"
                                rules={[{ required: true, message: 'Зип код оруулна уу!' }]}
                                label="Зип код"
                              >
                                <AddressCascader />
                              </Form.Item>
                            </Col>
                          </Row>
                        ) : null
                      }
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col span={12}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.senderFormVisibility !== currentValues.senderFormVisibility
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue('senderFormVisibility') ? (
                      <Row>
                        <Col span={24}>
                          <Form.Item
                            {...formItemLayout}
                            label="Хаяг дэлгэрэнгүй"
                            name="senderDetail"
                            rules={[{ required: true, message: 'Хаяг дэлгэрэнгүй оруулна уу!' }]}
                          >
                            <Input.TextArea rows={2} />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            {...formItemLayout}
                            label="what3words"
                            name="senderW3w"
                            style={{ marginBottom: 10 }}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    ) : null
                  }
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={20}>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  colon={false}
                  {...formItemLayout}
                  label="Хүлээн авагч"
                  style={{ marginBottom: 10, fontSize: 24, fontWeight: 'bold' }}
                >
                  <Checkbox
                    checked={sameAddress}
                    style={{ fontSize: 14, fontWeight: 'normal' }}
                    onChange={(event: any) => sameAddressChecked(event)}
                  >
                    илгээгчтэй ижил
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Row>
                  <Col span={24}>
                    <Form.Item label="Нэр" hidden={true} name="customerNo">
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...formItemLayout}
                      label="Нэр"
                      rules={[{ required: true, message: 'Нэр оруулна уу!' }]}
                      name="receiverFname"
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Row gutter={24}>
                      <Col span={24}>
                        <Row>
                          <Col span={16}>
                            <Form.Item
                              labelCol={{ md: { span: 12 } }}
                              wrapperCol={{ md: { span: 12 } }}
                              label="Утас"
                              name="receiverPhone1"
                              rules={[{ required: true, message: 'Утас 1 оруулна уу!' }]}
                            >
                              <Input placeholder="Утас 1" />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              style={{ marginBottom: 10, paddingLeft: 10 }}
                              name="receiverPhone2"
                            >
                              <Input placeholder="Утас 2" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24}>
                        <Form.Item
                          style={{ marginBottom: 10, paddingLeft: 10 }}
                          {...formItemLayout}
                          name="receiverZipcodes"
                          rules={[{ required: true, message: 'Зип код оруулна уу!' }]}
                          label="Зип код"
                        >
                          <AddressCascader />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col span={12}>
                <Row>
                  <Col span={24}>
                    <Form.Item
                      {...formItemLayout}
                      label="Хаяг дэлгэрэнгүй"
                      name="receiverDetail"
                      rules={[{ required: true, message: 'Хаяг дэлгэрэнгүй оруулна уу!' }]}
                    >
                      <Input.TextArea rows={2} />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      {...formItemLayout}
                      label="what3words"
                      name="receiverW3w"
                      style={{ marginBottom: 10 }}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col span={20}>
            <Row>
              <Col span={8}>
                <Form.Item
                  {...formItemLayout}
                  label="Хүргэлтийн төрөл"
                  name="deliveryType"
                  rules={[{ required: true, message: 'Хүргэлтийн төрөл сонгоно уу!' }]}
                  style={{ marginBottom: 10 }}
                >
                  <Select options={deliveryTypeOptions} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  {...formItemLayout}
                  label="Авах Огноо"
                  name="targetDate"
                  rules={[
                    { type: 'object', required: true, message: 'Хүлээн авах огноог оруулна уу!' },
                  ]}
                  style={{ marginBottom: 10 }}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  {...formItemLayout}
                  rules={[{ required: true, message: 'Төлбөрийн төрөл сонгоно уу!' }]}
                  name="paymentTypeCd"
                  label="Төлбөр"
                  style={{ marginBottom: 10 }}
                >
                  <Select style={{ width: '100%' }} placeholder="Төлбөр" options={paymentOptions} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row style={{ marginTop: 10, marginBottom: 10 }}>
          <Col span={20}>
            <Table bordered={true} pagination={false} columns={columns} dataSource={items} />
          </Col>
        </Row>
        <Row gutter={24} style={{ marginTop: 10, marginBottom: 10 }}>
          <Col span={20}>
            <Row>
              <Col span={12}>
                <Button color="primary" variant="link" onClick={addOrderLineItem}>
                  <PlusOutlined /> Хайрцаг нэмэх
                </Button>
              </Col>
              <Col span={12}>
                <span style={{ fontSize: 18, fontWeight: 'bold', float: 'right' }}>
                  {' '}
                  Нийт төлбөр: {priceFormat(totalAmount)}₮
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row style={{ marginTop: 10, marginBottom: 10 }}>
          <Col span={20}>
            <Form.Item>
              <Flex justify="end" gap={24}>
                <Button
                  //onClick={() => navigate(-1)}
                  htmlType="button"
                >
                  Буцах
                </Button>
                <Button loading={loading} type="primary" htmlType="submit">
                  Хадгалах
                </Button>
              </Flex>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default OrderUpdate;
